import type { EPCR } from '@/types';
import { NEMSIS_ELEMENTS, type NemsisElement, type NemsisUsage } from '@/lib/nemsis-elements';
import { activeSets, conditionallyRequiredElements, type ConditionalSet } from '@/lib/conditional-cascade';

/**
 * nemsis-completeness.ts — Foundation v1.0 §9.2.
 *
 * Converts the current EPCR + the active conditional sets into a
 * checklist keyed by NEMSIS element: which Mandatory / Required /
 * Recommended elements have a value, which are blank, and which can
 * be satisfied with a documented NOT-value ("Not Recorded",
 * "Not Applicable", etc.).
 *
 * The checker operates on the subset of NEMSIS elements that have
 * a natural binding into the current EPCR type. As the platform
 * adds UI for more elements, extend FIELD_BINDINGS below — anything
 * absent from that map is treated as "not yet captured in the UI"
 * and silently skipped by the Post-time validator.
 */

export type FieldStatus =
  | 'present'              // has a real value
  | 'marked_not_value'     // explicitly set to "Not Recorded" / "Not Applicable"
  | 'missing';             // blank and no NOT-value documented

export interface ElementCheck {
  element: NemsisElement;
  status: FieldStatus;
  usage: NemsisUsage;
  /** Short human-readable label for the missing thing (used in the modal). */
  label: string;
  /** True if this element is only required because of an active conditional set. */
  conditional: boolean;
  /** The active set that elevated this element to Required, if any. */
  requiredBy?: ConditionalSet;
}

export interface CompletenessSummary {
  /** True when no Mandatory or active-Required element is missing. */
  passesPost: boolean;
  checks: ElementCheck[];
  missingMandatory: ElementCheck[];
  missingRequired: ElementCheck[];
  missingRecommended: ElementCheck[];
  activeSets: ConditionalSet[];
  /** Flat list of element IDs actually evaluated (subset of 142). */
  evaluatedElementIds: string[];
}

// ------------------------------------------------------------------
// Field bindings — NEMSIS element → EPCR predicate.
//
// Each binding knows how to read a value from the EPCR and decide
// whether it counts as `present`, `marked_not_value`, or `missing`.
// Binding set is intentionally partial; expand as UI coverage grows.
// ------------------------------------------------------------------
type Binding = {
  label: string;
  read: (epcr: EPCR) => { status: FieldStatus };
};

const NOT_VALUES = new Set([
  'Not Applicable', 'Not Recorded', 'Not Reporting', 'Not Known', 'Unknown',
]);

const isPresent = (v: unknown): boolean => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
};

const stringBinding = (label: string, read: (e: EPCR) => string | null | undefined): Binding => ({
  label,
  read: (e) => {
    const v = read(e);
    if (typeof v === 'string' && NOT_VALUES.has(v)) return { status: 'marked_not_value' };
    return { status: isPresent(v) ? 'present' : 'missing' };
  },
});

const arrayBinding = (label: string, read: (e: EPCR) => unknown[] | undefined | null): Binding => ({
  label,
  read: (e) => ({ status: isPresent(read(e)) ? 'present' : 'missing' }),
});

/**
 * Map of NEMSIS element ID → binding. Only elements whose data can
 * be found on EPCR today. Missing IDs from this map are skipped.
 */
const FIELD_BINDINGS: Record<string, Binding> = {
  // Response
  'eResponse.13': stringBinding('Unit Number',       (e) => e.unit_id),
  'eResponse.14': stringBinding('Unit Call Sign',    (e) => e.unit_id),

  // Dispatch
  'eDispatch.01': stringBinding('Complaint Reported by Dispatch', (e) => e.dispatch_reason),

  // Times
  'eTimes.12':    stringBinding('Transfer of Care Date/Time',     (e) => e.toc_completed_at),

  // Patient
  'ePatient.08':  stringBinding('Patient Home State', (e) => e.patient_state),
  'ePatient.09':  stringBinding('Patient Home ZIP',   (e) => e.patient_zip),
  'ePatient.13':  stringBinding('Gender',             (e) => e.patient_gender),
  'ePatient.14':  arrayBinding('Race',                (e) => e.patient_race ? [e.patient_race] : []),

  // Situation (trigger layer)
  'eSituation.02': stringBinding('Possible Injury',           (e) => e.possible_injury ?? null),
  'eSituation.09': stringBinding('Primary Symptom',           (e) => e.chief_complaint),
  'eSituation.11': stringBinding("Provider's Primary Impression", (e) => e.primary_impression),
  'eSituation.12': arrayBinding("Secondary Impressions",      (e) => e.secondary_impressions),

  // Injury (trauma set)
  'eInjury.01':   arrayBinding('Cause of Injury',         (e) => e.injury_causes),
  'eInjury.03':   arrayBinding('Red Triage Criteria',     (e) => e.trauma_triage_red),
  'eInjury.04':   arrayBinding('Yellow Triage Criteria',  (e) => e.trauma_triage_yellow),

  // History
  'eHistory.17':  arrayBinding('Alcohol / Drug Use Indicators', (e) => e.alcohol_drug_indicators),

  // Disposition
  'eDisposition.27': stringBinding('Unit Disposition',   (e) => e.transport_decision ?? null),
  'eDisposition.30': stringBinding('Transport Disposition', (e) => e.transport_decision ?? null),
  'eDisposition.21': stringBinding('Type of Destination', (e) => e.destination_hospital),
};

// ------------------------------------------------------------------
export function checkCompleteness(epcr: EPCR): CompletenessSummary {
  const sets = activeSets(epcr);
  const conditionallyRequiredIds = new Set(conditionallyRequiredElements(epcr));

  const checks: ElementCheck[] = [];
  for (const [elementId, binding] of Object.entries(FIELD_BINDINGS)) {
    const element = NEMSIS_ELEMENTS[elementId];
    if (!element) continue;

    // If the element is conditional and none of its gating sets are
    // active, skip it (don't flag as missing).
    if (element.conditional?.length) {
      const activeSetIds = new Set(sets.map((s) => s.id));
      const anyActive = element.conditional.some((setId) => activeSetIds.has(setId as never));
      if (!anyActive) continue;
    }

    const { status } = binding.read(epcr);
    const requiredBy = conditionallyRequiredIds.has(elementId)
      ? sets.find((s) => s.elements.includes(elementId))
      : undefined;

    checks.push({
      element,
      status,
      usage: element.usage,
      label: binding.label,
      conditional: !!element.conditional?.length,
      requiredBy,
    });
  }

  const missing = checks.filter((c) => c.status === 'missing');
  const missingMandatory    = missing.filter((c) => c.usage === 'Mandatory');
  const missingRequired     = missing.filter((c) => c.usage === 'Required');
  const missingRecommended  = missing.filter((c) => c.usage === 'Recommended');

  return {
    passesPost: missingMandatory.length === 0,
    checks,
    missingMandatory,
    missingRequired,
    missingRecommended,
    activeSets: sets,
    evaluatedElementIds: checks.map((c) => c.element.id),
  };
}

/**
 * Apply a "Not Recorded" / "Not Applicable" NOT-value to a single
 * field binding. Used by the Post-time modal when the crew wants
 * to acknowledge a Required-but-not-Mandatory element is unknown.
 *
 * Returns the Partial<EPCR> patch; caller persists via onUpdate.
 */
export function notValuePatch(elementId: string, value: 'Not Recorded' | 'Not Applicable'): Partial<EPCR> {
  switch (elementId) {
    case 'ePatient.08': return { patient_state: value };
    case 'ePatient.09': return { patient_zip: value };
    case 'ePatient.13': return { patient_gender: value };
    case 'ePatient.14': return { patient_race: value };
    case 'eSituation.02': return { possible_injury: 'unknown' };
    case 'eInjury.01':   return { injury_causes: [] };
    case 'eInjury.03':   return { trauma_triage_red: [] };
    case 'eInjury.04':   return { trauma_triage_yellow: [] };
    case 'eHistory.17':  return { alcohol_drug_indicators: [
      value === 'Not Applicable'
        ? 'Unable to assess (patient unconscious, language barrier, etc.)'
        : 'Unable to assess (patient unconscious, language barrier, etc.)',
    ] };
    default:
      return {};
  }
}
